<template>
  <div>
    <div class="ps-4">
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="CART">
      </section-header>
    </div>

    <v-data-table :calculate-widths="true"
                  no-data-text="Aucune donnée"
                  :headers="headers"
                  :items="simulations"
                  :items-per-page="-1"
                  :height="300"
                  class="white"
                  disable-pagination
                  fixed-header
                  hide-default-footer
                  mobile-breakpoint="100">
      <template v-slot:[`item.createdAt`]="{ item }">
        {{ formatDateHeure(item.createdAt) }}
      </template>
      <template v-slot:[`item.newUser`]="{ item }">
        {{ formatBoolean(item.newUser) }}
      </template>
      <template v-slot:[`item.artiste`]="{ item }">
        {{ formatBoolean(item.artiste) }}
      </template>
      <template v-slot:[`item.cadre`]="{ item }">
        {{ formatBoolean(item.cadre) }}
      </template>
      <template v-slot:[`item.mode`]="{ item }">
        {{ formatMode(item.mode) }}
      </template>
    </v-data-table>
    <div class="d-flex flex-row-reverse">
      <loader-button class="ms-2"
                     type="submit"
                     :label="saveLabel"
                     :disabled="!canExport"
                     :loading="exportLoading"
                     @callback="exportSimu">
      </loader-button>
    </div>
  </div>

</template>

<script lang="ts">
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import { Extended } from '@/framework/base/classes/Schema';
  import Simulation, { StatSimu } from '@/generated/classes/Simulation';
  import Files, { DownloadData } from '@/generated/classes/Files';
  import { getBlob } from '@/framework/files/helpers/FileTypes';
  import { defineComponent, type PropType } from "vue";

  export default defineComponent({
    components: {
      SectionHeader,
      LoaderButton
    },
    props: {
      hideTitle: {
        type: Boolean
      },
      extended: {
        type: Boolean
      },
      dialog: {
        type: Boolean
      },
      simulation: {
        type: Object as PropType<StatSimu>,
        required: true
      }
    },
    data() {
      const mSimulations: Simulation[] = [];

      return {
        canExport: false,
        exportLoading: false,
        dialogOpen: false,
        headers: [
          { title: 'Date', key: 'createdAt', sortable: true },
          { title: 'Nom', key: 'nom', sortable: true },
          { title: 'Prénom', key: 'prenom', sortable: true },
          { title: 'Adresse mail', key: 'email', sortable: true },
          { title: 'Nouveau mail', key: 'newUser', sortable: true },
          { title: 'Convention', key: 'convention', sortable: true },
          { title: 'Artiste', key: 'artiste', sortable: true },
          { title: 'Cadre', key: 'cadre', sortable: true },
          { title: 'Durée', key: 'duree', sortable: true },
          { title: 'Montant', key: 'montant', sortable: true },
          { title: 'Mode', key: 'mode', sortable: true }
        ],
        sectionName: Sections.LIST_SIMU,
        mYear: 0,
        mMonth: 0,
        mSimulations
      };
    },
    computed: {
      saveLabel() {
        return RessourceFwkButton(FwkButtons.EXPORTER).label;
      },
      errorTxt() {
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },
      ressource() {
        return RessourceGenSection(GenSection.LIST_SIMU);
      },
      simulations(): Simulation[] {
        const period = new Date(this.simulation.period);
        if (period.getFullYear() != this.mYear || period.getMonth() != this.mMonth) {
          this.setYear(period);
          this.setMonth(period);
          this.loadSimulationDetails();
        }
        return this.mSimulations;
      }
    },
    methods: {
      setYear(value: Date) {
        this.mYear = value.getFullYear();
      },
      setMonth(value: Date) {
        this.mMonth = value.getMonth();
      },
      loadSimulationDetails() {
        Simulation.details(this.simulation).then((res) => {
          this.canExport = res.length > 0;
          this.mSimulations = res;
        })
      },
      formatDateHeure(value: string): string {
        return new Date(value).toLocaleString();
      },
      formatBoolean(value: boolean): string {
        return value ? 'X' : '';
      },
      formatMode(value: number): string {
        if (value == 2) {
          return 'BRUT';
        } else if (value == 3) {
          return 'NET';
        } else if (value == 4) {
          return 'TOTAL';
        } else {
          return '';
        }
      },
      exportSimu(): void {
        const data = new DownloadData();
        this.exportLoading = true;
        data.service = 'Simulations';
        data.type.extension = 'xlsx';
        data.data = this.simulation.period.toString();

        const year = new Date(this.simulation.period).getFullYear()
        const month = new Date(this.simulation.period).getMonth() + 1;
        const strMonth = (month < 10 ? '0' : '') + month;
        const fileName = `${year}-${strMonth}-${data.service}`; // set table name as default file name
        this.canExport = false;

        Files.download(data)
          .then((buffer) => {
            const blob = getBlob(buffer, data.type.extension);
            const file = window.URL.createObjectURL(blob);
            const docUrl = document.createElement('a');
            docUrl.setAttribute('href', file);
            docUrl.setAttribute('download', `${fileName}.${data.type.extension}`);
            document.body.appendChild(docUrl);
            docUrl.click();
            this.exportLoading = false;
          });
      },
      setExtended(extended: Extended): void {
        this.$emit('extended', extended);
      },
      closeDialog() {
        this.$emit('closeDialog');
      },
      openDialog() {
        this.dialogOpen = true;
      }
    }
  })

</script>
